import React, { useCallback, useEffect, useRef, useState } from 'react';
import { screenToCanvasPoint } from '../utils/canvasViewport';
import './CanvasMarkup.css';

const LASER_LIFETIME_MS = 700;
const ERASER_RADIUS_PX = 14;

const pointToSegmentDistanceSquared = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return ((point.x - start.x) ** 2) + ((point.y - start.y) ** 2);
  }

  const progress = Math.max(0, Math.min(1, (
    ((point.x - start.x) * dx) + ((point.y - start.y) * dy)
  ) / ((dx * dx) + (dy * dy))));
  const nearestX = start.x + progress * dx;
  const nearestY = start.y + progress * dy;
  return ((point.x - nearestX) ** 2) + ((point.y - nearestY) ** 2);
};

export const eraseMarkupStrokesAtPoint = (strokes, point, radius) => {
  const radiusSquared = radius ** 2;
  return strokes.filter((stroke) => {
    if (stroke.points.length === 1) {
      return pointToSegmentDistanceSquared(point, stroke.points[0], stroke.points[0]) > radiusSquared;
    }
    return !stroke.points.slice(1).some((end, index) => (
      pointToSegmentDistanceSquared(point, stroke.points[index], end) <= radiusSquared
    ));
  });
};

const pointsToPath = (points) => {
  if (!points.length) return '';
  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y} L ${points[0].x} ${points[0].y}`;
  }
  return points.map((point, index) => (
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  )).join(' ');
};

const TOOL_BUTTONS = [
  { id: 'pan', label: 'Hand', icon: '↖' },
  { id: 'pen', label: 'Pen', icon: '✎' },
  { id: 'laser', label: 'Laser pointer', icon: '●' },
  { id: 'eraser', label: 'Eraser', icon: '⌫' },
];

const CanvasMarkup = ({ viewport }) => {
  const [tool, setTool] = useState('pan');
  const [strokes, setStrokes] = useState([]);
  const [activeStroke, setActiveStroke] = useState(null);
  const [laserStrokes, setLaserStrokes] = useState([]);
  const activeStrokeRef = useRef(null);
  const erasingRef = useRef(false);
  const nextStrokeIdRef = useRef(1);
  const laserTimersRef = useRef(new Set());

  useEffect(() => () => {
    laserTimersRef.current.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setTool('pan');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getCanvasPoint = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return screenToCanvasPoint({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }, viewport);
  }, [viewport]);

  const eraseAtPoint = useCallback((point) => {
    const zoom = Number(viewport.zoom) || 1;
    setStrokes((current) => eraseMarkupStrokesAtPoint(
      current,
      { x: point.left, y: point.top },
      ERASER_RADIUS_PX / zoom
    ));
  }, [viewport.zoom]);

  const finishStroke = useCallback(() => {
    const stroke = activeStrokeRef.current;
    activeStrokeRef.current = null;
    erasingRef.current = false;
    setActiveStroke(null);
    if (!stroke) return;

    if (stroke.tool === 'pen') {
      setStrokes((current) => [...current, stroke]);
      return;
    }

    setLaserStrokes((current) => [...current, stroke]);
    const timer = setTimeout(() => {
      setLaserStrokes((current) => current.filter((candidate) => candidate.id !== stroke.id));
      laserTimersRef.current.delete(timer);
    }, LASER_LIFETIME_MS);
    laserTimersRef.current.add(timer);
  }, []);

  const handlePointerDown = (event) => {
    if (tool === 'pan' || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = getCanvasPoint(event);

    if (tool === 'eraser') {
      erasingRef.current = true;
      eraseAtPoint(point);
      return;
    }

    const stroke = {
      id: nextStrokeIdRef.current++,
      tool,
      points: [{ x: point.left, y: point.top }],
    };
    activeStrokeRef.current = stroke;
    setActiveStroke(stroke);
  };

  const handlePointerMove = (event) => {
    if (tool === 'pan') return;
    if (erasingRef.current) {
      eraseAtPoint(getCanvasPoint(event));
      return;
    }

    const stroke = activeStrokeRef.current;
    if (!stroke) return;
    const point = getCanvasPoint(event);
    const nextStroke = {
      ...stroke,
      points: [...stroke.points, { x: point.left, y: point.top }],
    };
    activeStrokeRef.current = nextStroke;
    setActiveStroke(nextStroke);
  };

  const eraseAll = () => {
    laserTimersRef.current.forEach(clearTimeout);
    laserTimersRef.current.clear();
    activeStrokeRef.current = null;
    setActiveStroke(null);
    setStrokes([]);
    setLaserStrokes([]);
  };

  const renderedLaserStrokes = [
    ...laserStrokes,
    ...(activeStroke?.tool === 'laser' ? [activeStroke] : []),
  ];

  return (
    <>
      <div className="dashboard-overlay">
        <div className="canvas-markup-toolbar" aria-label="Canvas markup tools">
          {TOOL_BUTTONS.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className={tool === candidate.id ? 'active' : ''}
              aria-label={candidate.label}
              aria-pressed={tool === candidate.id}
              title={candidate.id === 'pan' ? 'Hand — interact with the canvas and widgets' : candidate.label}
              onClick={() => setTool(candidate.id)}
            >
              <span aria-hidden="true">{candidate.icon}</span>
            </button>
          ))}
          <span className="canvas-markup-divider" aria-hidden="true" />
          <button
            type="button"
            aria-label="Erase all markup"
            title="Erase all markup"
            onClick={eraseAll}
            disabled={strokes.length === 0 && laserStrokes.length === 0 && !activeStroke}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>

      <svg
        className={`canvas-markup-layer tool-${tool}`}
        aria-label="Canvas markup layer"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
        onMouseDown={(event) => {
          if (tool !== 'pan') event.stopPropagation();
        }}
      >
        <g transform={`translate(${viewport.x} ${viewport.y}) scale(${viewport.zoom})`}>
          {strokes.map((stroke) => (
            <path
              key={stroke.id}
              data-markup-stroke="pen"
              d={pointsToPath(stroke.points)}
              className="canvas-pen-stroke"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {activeStroke?.tool === 'pen' && (
            <path
              data-active-markup-stroke="pen"
              d={pointsToPath(activeStroke.points)}
              className="canvas-pen-stroke"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {renderedLaserStrokes.map((stroke) => (
            <path
              key={stroke.id}
              data-markup-stroke="laser"
              d={pointsToPath(stroke.points)}
              className="canvas-laser-stroke"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </g>
      </svg>
    </>
  );
};

export default CanvasMarkup;
